import type { ActivityEvent } from "activity-logger";

const AUTH_SENSITIVE_PATHS = new Set(["/callback"]);
const MAX_SIGNED_INT32 = 2_147_483_647;
const RECEIVER_SAFE_TIMESTAMP = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;

export const STATIC_ACTIVITY_PATHS = [
  "/",
  "/callback",
  "/run-history",
  "/run-report-card",
  "/demand",
  "/demand_preview",
  "/demand-walk-single",
  "/demand-plan",
  "/supply",
  "/supply-walk",
  "/supply-walk-single",
  "/supply-walk-single-for-hubs",
  "/po-export",
  "/chat",
  "/profile",
  "/forecast-tracking",
  "/three-forecast-tracking",
  "/inventory-health",
  "/inventory-health-with-transfers",
  "/inventory-health-for-hubs",
  "/inventory-wos",
  "/transfer-recommendations-hub-to-spoke",
  "/inventory-health-mrp",
  "/raw-inventory-health",
  "/inventory",
  "/transfer-planning",
  "/transfer-recommendations",
  "/avanti-card-runs",
  "/purchase-order-export-peach-and-lily",
  "/po-inbox",
  "/my-team",
  "/sku-cutovers",
  "/purchasing-calendar",
  "/container-mapping",
  "/help",
  "/help/publish",
  "/manufacturer-stock",
] as const;

const staticActivityPaths = new Set<string>(STATIC_ACTIVITY_PATHS);

export const DYNAMIC_ACTIVITY_ROUTE_TEMPLATES = ["/help/:slug"] as const;

const DYNAMIC_ACTIVITY_PATHS: ReadonlyArray<{
  pattern: RegExp;
  template: string;
}> = [
  {
    pattern: /^\/help\/[^/]+$/,
    template: DYNAMIC_ACTIVITY_ROUTE_TEMPLATES[0],
  },
];

const activityPathname = (rawPath: string): string => {
  try {
    const path = new URL(rawPath, "https://activity.invalid").pathname || "/";
    const rooted = path.startsWith("/") ? path : `/${path}`;
    return rooted.length > 1 ? rooted.replace(/\/+$/, "") : rooted;
  } catch {
    const path = rawPath.split(/[?#]/, 1)[0] || "/";
    const rooted = path.startsWith("/") ? path : `/${path}`;
    return rooted.length > 1 ? rooted.replace(/\/+$/, "") : rooted;
  }
};

export const normalizeActivityPath = (rawPath: string): string => {
  const pathname = activityPathname(rawPath);
  if (staticActivityPaths.has(pathname)) return pathname;
  return (
    DYNAMIC_ACTIVITY_PATHS.find(({ pattern }) => pattern.test(pathname))
      ?.template ?? "/unknown"
  );
};

export const isActivityPathExcluded = (rawPath: string): boolean => {
  const path = activityPathname(rawPath);
  return AUTH_SENSITIVE_PATHS.has(path);
};

const receiverSafeOccurredAt = (value: string): boolean => {
  if (!RECEIVER_SAFE_TIMESTAMP.test(value) || Number(value.slice(0, 4)) === 0) {
    return false;
  }
  try {
    return new Date(value).toISOString() === value;
  } catch {
    return false;
  }
};

const receiverSafeRuntime = (value: number | null | undefined): boolean =>
  value === null ||
  value === undefined ||
  (Number.isSafeInteger(value) && value >= 0 && value <= MAX_SIGNED_INT32);

/**
 * The final browser privacy boundary. Only the v1 fields we intentionally send
 * are copied, and URL-shaped values are reduced to a path before serialization.
 */
export const sanitizeActivityEvent = (
  event: ActivityEvent,
  expectedCustomerId: string,
): ActivityEvent | null => {
  if (
    event.client !== expectedCustomerId ||
    isActivityPathExcluded(event.endpoint) ||
    !receiverSafeOccurredAt(event.occurredAt) ||
    !receiverSafeRuntime(event.frontEndRuntimeMs) ||
    !receiverSafeRuntime(event.backEndRuntimeMs)
  ) {
    return null;
  }
  const endpoint = normalizeActivityPath(event.endpoint);

  return {
    app: "common",
    occurredAt: event.occurredAt,
    endpoint,
    url: endpoint,
    parameters: {},
    client: expectedCustomerId,
    frontEndRuntimeMs: event.frontEndRuntimeMs ?? null,
    backEndRuntimeMs: event.backEndRuntimeMs ?? null,
    isAjaxRefresh:
      typeof event.isAjaxRefresh === "boolean" ? event.isAjaxRefresh : false,
  };
};

export const sanitizeActivityEvents = (
  events: ActivityEvent[],
  expectedCustomerId: string,
): ActivityEvent[] =>
  events
    .map((event) => sanitizeActivityEvent(event, expectedCustomerId))
    .filter((event): event is ActivityEvent => event !== null);
